import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { checklistsApi, type ChecklistSchema } from '@/api/checklists.api';
import { formatApiMessage } from '@/api/client';
import { documentsApi } from '@/api/documents.api';
import type { EnvelopeView } from '@/api/envelopes.api';
import { ChecklistBuilder } from '@/components/checklist/ChecklistBuilder';
import { FileIcon } from '@/components/icons/Icons';
import { formatBytes } from '@/lib/format';

import { DocumentEditor } from './DocumentEditor';

const MAX_BYTES = 25 * 1024 * 1024;

const ACCEPTED_MIME = [
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.oasis.opendocument.text',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.oasis.opendocument.spreadsheet',
  'application/vnd.ms-powerpoint',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  'application/vnd.oasis.opendocument.presentation',
  'application/rtf',
  'text/rtf',
  'text/plain',
];

const ACCEPTED_ATTR = '.pdf,.doc,.docx,.odt,.xls,.xlsx,.ods,.ppt,.pptx,.odp,.rtf,.txt';

type Props = {
  envelope: EnvelopeView;
};

type Mode = 'upload' | 'create' | 'checklist' | 'edit';

export function DocumentsStep({ envelope }: Props) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [uploadPct, setUploadPct] = useState<number | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const [mode, setMode] = useState<Mode>('upload');
  const [editingDoc, setEditingDoc] = useState<{ id: string; html: string; title: string } | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const uploadMutation = useMutation({
    mutationFn: async (file: File) => {
      if (!ACCEPTED_MIME.includes(file.type) && !ACCEPTED_ATTR.split(',').some((ext) => file.name.toLowerCase().endsWith(ext.trim()))) {
        throw new Error(t('documents_step.unsupported_type'));
      }
      if (file.size > MAX_BYTES) throw new Error(t('documents_step.too_large'));
      setUploadPct(0);
      return documentsApi.upload(envelope.id, file, setUploadPct);
    },
    onSuccess: () => {
      setUploadPct(null);
      qc.invalidateQueries({ queryKey: ['envelopes', 'detail', envelope.id] });
      toast.success(t('documents_step.uploaded_toast'));
    },
    onError: (err) => {
      setUploadPct(null);
      toast.error(formatApiMessage(err));
    },
  });

  const createFromHtmlMutation = useMutation({
    mutationFn: (payload: { title: string; html: string }) =>
      documentsApi.createFromHtml(envelope.id, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['envelopes', 'detail', envelope.id] });
      toast.success(t('documents_step.created_toast'));
      setMode('upload');
    },
    onError: (err) => toast.error(formatApiMessage(err)),
  });

  const updateHtmlMutation = useMutation({
    mutationFn: (payload: { id: string; title: string; html: string }) =>
      documentsApi.updateHtml(payload.id, { title: payload.title, html: payload.html }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['envelopes', 'detail', envelope.id] });
      toast.success(t('documents_step.updated_toast'));
      setMode('upload');
      setEditingDoc(null);
    },
    onError: (err) => toast.error(formatApiMessage(err)),
  });

  const openEditor = async (docId: string) => {
    try {
      const { html, title } = await documentsApi.getHtml(docId);
      setEditingDoc({ id: docId, html, title });
      setMode('edit');
    } catch (err) {
      toast.error(formatApiMessage(err));
    }
  };

  // A checklist authored on this envelope — reloaded so re-entering the tab
  // reopens the builder on the existing form instead of a blank one.
  const checklistQuery = useQuery({
    queryKey: ['checklists', 'envelope', envelope.id],
    queryFn: () => checklistsApi.getByEnvelope(envelope.id),
  });
  const checklist = checklistQuery.data ?? null;

  const saveChecklistMutation = useMutation({
    mutationFn: (payload: { title: string; description: string; schema: ChecklistSchema }) =>
      checklist
        ? checklistsApi.updateTemplate(checklist.id, payload)
        : checklistsApi
            .createForEnvelope(envelope.id, payload)
            .then((res) => res.template),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['envelopes', 'detail', envelope.id] });
      qc.invalidateQueries({ queryKey: ['checklists', 'envelope', envelope.id] });
      qc.invalidateQueries({ queryKey: ['checklists', 'templates'] });
      toast.success(t('checklist.saved_toast'));
      setMode('upload');
    },
    onError: (err) => toast.error(formatApiMessage(err)),
  });

  const removeMutation = useMutation({
    mutationFn: documentsApi.delete,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['envelopes', 'detail', envelope.id] });
      toast.success(t('documents_step.removed_toast'));
    },
    onError: (err) => toast.error(formatApiMessage(err)),
  });

  const replaceMutation = useMutation({
    mutationFn: ({ id, file }: { id: string; file: File }) =>
      documentsApi.replaceFile(id, file),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ['envelopes', 'detail', envelope.id] });
      toast.success(t('documents_step.replaced_toast'));
      if (res.pageMismatch) {
        toast.warning(t('documents_step.replaced_page_mismatch'));
      }
    },
    onError: (err) => toast.error(formatApiMessage(err)),
  });

  const replaceFileInputsRef = useRef<Record<string, HTMLInputElement | null>>({});

  const handleFiles = (files: FileList | null) => {
    if (!files || files.length === 0) return;
    Array.from(files).forEach((file) => uploadMutation.mutate(file));
  };

  return (
    <div>
      <h3 className="text-base font-semibold text-gray-900 mb-1">{t('documents_step.title')}</h3>
      <p className="text-[13px] text-muted mb-4">{t('documents_step.subtitle')}</p>

      {envelope.documentNumber && (
        <div className="mb-4 flex items-center gap-2 rounded-md border border-primary/30 bg-primary-50 px-3 py-2">
          <span className="text-[11px] uppercase font-semibold text-primary tracking-wider">
            {t('documents_step.doc_number')}
          </span>
          <code className="text-[13px] font-mono font-semibold text-gray-900">
            {envelope.documentNumber}
          </code>
        </div>
      )}

      <div className="flex gap-1 mb-4 border-b border-border">
        <ModeTab active={mode === 'upload'} onClick={() => setMode('upload')}>
          📤 {t('documents_step.tab_upload')}
        </ModeTab>
        <ModeTab active={mode === 'create'} onClick={() => setMode('create')}>
          ✍️ {t('documents_step.tab_create')}
        </ModeTab>
        <ModeTab active={mode === 'checklist'} onClick={() => setMode('checklist')}>
          ☑️ {t('documents_step.tab_checklist')}
        </ModeTab>
      </div>

      {mode === 'upload' ? (
        <>
          <div
            onDragOver={(e) => {
              e.preventDefault();
              setDragOver(true);
            }}
            onDragLeave={() => setDragOver(false)}
            onDrop={(e) => {
              e.preventDefault();
              setDragOver(false);
              handleFiles(e.dataTransfer.files);
            }}
            onClick={() => fileInputRef.current?.click()}
            className={`card border-2 border-dashed cursor-pointer transition-colors ${
              dragOver ? 'border-primary bg-primary-50' : 'border-border-strong'
            }`}
          >
            <div className="text-center py-7">
              <FileIcon className="w-8 h-8 mx-auto text-muted mb-2" />
              <div className="font-medium text-gray-900">{t('documents_step.drop_here')}</div>
              <div className="text-sm text-muted mt-1">
                {t('documents_step.or_click')} · {t('documents_step.mixed_limit')}
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept={ACCEPTED_ATTR}
                multiple
                className="hidden"
                onChange={(e) => handleFiles(e.target.files)}
              />
            </div>
          </div>

          {uploadPct !== null && (
            <div className="mt-3">
              <div className="text-xs text-muted mb-1">
                {t('documents_step.uploading')} {uploadPct}%
              </div>
              <div className="progress">
                <div className="progress-bar" style={{ width: `${uploadPct}%` }} />
              </div>
            </div>
          )}
        </>
      ) : mode === 'checklist' ? (
        checklistQuery.isLoading ? (
          <div className="card text-center text-[13px] text-muted">{t('common.loading')}</div>
        ) : (
          <>
            <div className="mb-3 rounded-md border border-primary/30 bg-primary-50 px-3 py-2 text-[12px] text-gray-700">
              {t('checklist.wizard_hint')}
            </div>
            <ChecklistBuilder
              key={checklist?.id ?? 'new'}
              initialTitle={checklist?.title ?? envelope.title}
              initialDescription={checklist?.description ?? ''}
              initialSchema={checklist?.schema}
              saving={saveChecklistMutation.isPending}
              saveLabel={
                checklist
                  ? (t('checklist.update_and_attach') as string)
                  : (t('checklist.save_and_attach') as string)
              }
              onSave={(payload) => saveChecklistMutation.mutate(payload)}
              onCancel={() => setMode('upload')}
            />
          </>
        )
      ) : mode === 'edit' && editingDoc ? (
        <DocumentEditor
          initialHtml={editingDoc.html}
          initialTitle={editingDoc.title}
          saving={updateHtmlMutation.isPending}
          saveLabel={t('documents_step.update_doc') as string}
          onSave={(payload) =>
            updateHtmlMutation.mutate({
              id: editingDoc.id,
              title: payload.title,
              html: payload.html,
            })
          }
          onCancel={() => {
            setMode('upload');
            setEditingDoc(null);
          }}
        />
      ) : (
        <DocumentEditor
          saving={createFromHtmlMutation.isPending}
          onSave={(payload) => createFromHtmlMutation.mutate(payload)}
          onCancel={() => setMode('upload')}
        />
      )}

      {envelope.documents.length > 0 && (
        <ul className="mt-5 space-y-2">
          {envelope.documents.map((doc) => (
            <li key={doc.id} className="card flex items-center gap-3 !py-3">
              <div className="w-9 h-9 rounded-md bg-primary-50 text-primary flex items-center justify-center shrink-0">
                <FileIcon />
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-medium text-gray-900 truncate flex items-center gap-2">
                  {doc.originalName}
                  {doc.sourceType === 'html' && (
                    <span className="text-[10px] uppercase tracking-wide bg-primary-50 text-primary rounded px-1.5 py-0.5">
                      {t('documents_step.html_source')}
                    </span>
                  )}
                </div>
                <div className="text-xs text-muted">
                  {doc.pagesCount} {t('documents_step.pages')} · {formatBytes(doc.fileSize)}
                  {envelope.documentNumber && (
                    <>
                      {' · '}
                      <code className="font-mono text-gray-700">{envelope.documentNumber}</code>
                    </>
                  )}
                </div>
              </div>
              {doc.sourceType === 'html' && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    openEditor(doc.id);
                  }}
                  className="btn btn-ghost btn-sm"
                  title={t('documents_step.edit_doc_hint') as string}
                >
                  {t('documents_step.edit_doc')}
                </button>
              )}
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  replaceFileInputsRef.current[doc.id]?.click();
                }}
                className="btn btn-ghost btn-sm"
                title={t('documents_step.replace_doc_hint') as string}
                disabled={replaceMutation.isPending}
              >
                {t('documents_step.replace_doc')}
              </button>
              <input
                ref={(el) => {
                  replaceFileInputsRef.current[doc.id] = el;
                }}
                type="file"
                accept="application/pdf"
                className="hidden"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) replaceMutation.mutate({ id: doc.id, file });
                  // Reset so picking the same filename again still triggers change.
                  e.target.value = '';
                }}
              />
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  if (confirm(t('documents_step.remove_confirm_named', { name: doc.originalName })))
                    removeMutation.mutate(doc.id);
                }}
                className="btn btn-ghost btn-sm text-danger"
                disabled={removeMutation.isPending}
              >
                {t('documents_step.remove_doc')}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function ModeTab({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`px-4 py-2 text-[13px] font-medium border-b-2 transition-colors ${
        active
          ? 'border-primary text-primary'
          : 'border-transparent text-muted hover:text-gray-900'
      }`}
    >
      {children}
    </button>
  );
}
